import { useMemo, useRef, useState, useEffect } from 'react';
import classNames from 'classnames';
import { Tooltip, capitalize } from '@crazyegginc/hatch';

import { useNotifications } from '/src/hooks';
import { LazyImg } from '/src/components/LazyImg';
import { deserializeTrigger } from '/src/features/goals/machines/wizard';
import { camelToSnake, capitalizeFirstLetter } from '/src/utils/string';
import { copyToClipboard, pickFields } from '/src/utils';
import { SupportLinks } from '/src/support';
import { RECORDINGS_FILTER_VERSION } from '/src/features/recordings/constants';
import { DEVICE_TYPES, SPECIAL_DATE_RANGES, LOGICALS } from '/src/features/_global/constants';
import {
  PIXEL_API_LIST,
  GOAL_TRIGGER_TYPES,
  ECOMMERCE_EVENTS_LIST_TYPE,
  ECOMMERCE_LIST,
  GOAL_VALUE_TYPES,
} from '/src/features/goals/constants';
import { safeJsonParse } from '../_global/utils';

function humanizePixelApi(api) {
  switch (api) {
    case PIXEL_API_LIST.TIKTOK:
      return 'TikTok';
    default:
      return capitalize(api);
  }
}

function humanizeEcommerceApi(api) {
  switch (api) {
    case ECOMMERCE_LIST.WIXAPI:
      return 'Wix';
    case ECOMMERCE_LIST.WOOCOMMERCE:
      return 'WooCommerce';
    case ECOMMERCE_LIST.GENERIC:
      return 'Other';
    default:
      return capitalize(api);
  }
}

const buildThumbnailArgs = (thumbnailUrl) => {
  if (!thumbnailUrl) return {};
  return {
    thumbnail_for_desktop: thumbnailUrl,
    thumbnail_for_phone: thumbnailUrl,
    thumbnail_for_tablet: thumbnailUrl,
  };
};

const linkSegment = (url) => {
  if (!url) return null;

  return {
    message: url,
    href: url,
    onHover: { type: 'link', arguments: [url] },
  };
};

const firstTriggerCondition = (trigger) => {
  const filter = safeJsonParse(trigger?.filter);
  if (!Array.isArray(filter?.conditions) || filter.conditions.length === 0) return null;
  return filter.conditions[0];
};

const buildSegmentsFromFilter = (trigger, thumbnailUrl) => {
  const condition = firstTriggerCondition(trigger);
  if (!condition) return [];

  const criteria = condition.criteria?.toLowerCase?.() ?? '';
  const values = Array.isArray(condition.values) ? condition.values : [];

  switch (criteria) {
    case 'code': {
      const code = values[0];
      return [
        { message: 'Triggers a script', onHover: { type: 'scriptInstructions' } },
        '|',
        {
          message: 'Copy script',
          onHover: { type: 'scriptInstructionsCopy', arguments: [code] },
          onClick: { type: 'copyUserScript', arguments: [code] },
        },
      ];
    }
    case 'url_visit': {
      const url = values[0]?.url ?? values[0];
      return ['Reaches', linkSegment(url)].filter(Boolean);
    }
    case 'click': {
      const click = values[0] || {};
      const valuePayload = click?.value ?? click;
      const page = click?.on_any_page ? 'any page' : linkSegment(click?.on_page);
      const wildcard =
        click?.on_any_page && typeof click?.on_page === 'string' && click.on_page.includes('*')
          ? `that matches ${click.on_page}`
          : null;

      return [
        {
          message: 'Clicks a button or a link',
          onHover: {
            type: 'editor',
            arguments: [{ ...valuePayload, ...buildThumbnailArgs(thumbnailUrl) }],
          },
        },
        'on',
        page,
        wildcard,
      ].filter(Boolean);
    }
    case 'form_submission': {
      const form = values[0] || {};
      const valuePayload = form?.value ?? form;
      const page = form?.on_any_page ? 'any page' : linkSegment(form?.on_page);
      const wildcard =
        form?.on_any_page && typeof form?.on_page === 'string' && form.on_page.includes('*')
          ? `that matches ${form.on_page}`
          : null;

      return [
        {
          message: 'Fills a form',
          onHover: {
            type: 'editor',
            arguments: [{ ...valuePayload, ...buildThumbnailArgs(thumbnailUrl) }],
          },
        },
        'on',
        page,
        wildcard,
      ].filter(Boolean);
    }
    case 'pixel_event': {
      const pixel = values[0] ?? {};
      return [
        {
          message: 'Triggers a conversion pixel',
          onHover: { type: 'pixel_event', arguments: [pixel] },
        },
      ];
    }
    case 'api':
      return [{ message: 'Called via API' }];
    case 'ecommerce_event': {
      const ecommerce = values[0] ?? {};
      return [
        {
          message: 'Completes an ecommerce event',
          onHover: { type: 'ecommerce_event', arguments: [ecommerce] },
        },
      ];
    }
    default:
      return [];
  }
};

const buildDisplayDataFromTriggers = (goal, triggers, { includeDeleted = false } = {}) => {
  const triggerList = Array.isArray(triggers) ? triggers : [];
  const filteredTriggers = includeDeleted ? triggerList : triggerList.filter((trigger) => !trigger.deletedAt);
  const thumbnailUrl = goal?.thumbnailUrl ?? goal?.thumbnail_url;

  return {
    id: goal?.id ?? 'goal',
    triggers: filteredTriggers.map((trigger, index) => ({
      id: trigger.id,
      name: trigger.name,
      auto_name: trigger.autoName ?? trigger.auto_name,
      index: index + 1,
      segments: buildSegmentsFromFilter(trigger, thumbnailUrl),
    })),
  };
};

function triggersFromFilters(goal, device = DEVICE_TYPES.DESKTOP) {
  const parsed = buildDisplayDataFromTriggers(goal, goal?.triggers);

  if (!parsed.triggers?.length) {
    return null;
  }

  const triggers = parsed.triggers;

  const humanizedTriggers = triggers.map((trigger, triggerIndex) => {
    return trigger.segments
      .map((segment, index) => {
        if (typeof segment === 'string') {
          const isLink = segment.match(/https?:\/\//);
          return (
            <span
              key={`${parsed.id}_${triggerIndex}_${index}`}
              className={classNames({
                'shrink-0': !isLink,
                'min-w-0 truncate': isLink,
              })}
            >
              {segment}
            </span>
          );
        }
        if (segment && segment?.href) {
          return (
            <div className="relative min-w-0" key={`Trigger-${trigger.index}-Segment-${index}`}>
              <GoalLinkURL url={segment.href} message={segment.message} />
            </div>
          );
        }
        if (segment && segment?.onHover) {
          if (segment.onHover.type === 'editor') {
            return (
              <span className="shrink-0" key={`Trigger-${trigger.index}-Segment-${index}`}>
                <Tooltip
                  interactive={true}
                  darkTheme={false}
                  placement="bottom"
                  tooltipContent={
                    <LazyImg
                      className="h-[88px] w-[138px] !rounded-none"
                      alt="goal thumbnail"
                      src={segment.onHover.arguments?.[0]?.[`thumbnail_for_${device.toLowerCase()}`] ?? null}
                      width="138px"
                      height="88px"
                    />
                  }
                >
                  <span className="cursor-pointer text-2xs font-normal underline decoration-cadet-blue-500 decoration-dashed underline-offset-2">
                    {segment.message}
                  </span>
                </Tooltip>
              </span>
            );
          } else if (segment.onHover.type === 'scriptInstructions') {
            return (
              <span className="shrink-0" key={`Trigger-${trigger.index}-Segment-${index}`}>
                <Tooltip
                  interactive={true}
                  darkTheme={true}
                  placement="bottom"
                  tooltipContent={
                    <div className="w-40">
                      Read our{' '}
                      <a
                        href={SupportLinks.script.ce2Functions}
                        className="text-link"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        help article
                      </a>{' '}
                      on setting up code goals.
                    </div>
                  }
                >
                  <span className="cursor-pointer text-2xs font-normal underline decoration-cadet-blue-500 decoration-dashed underline-offset-2">
                    {segment.message}
                  </span>
                </Tooltip>
              </span>
            );
          } else if (segment.onHover.type === 'scriptInstructionsCopy') {
            return (
              <CopyCodeLink segment={segment} trigger={trigger} key={`Trigger-${trigger.index}-Segment-${index}`} />
            );
          } else if (segment.onHover.type === 'pixel_event') {
            return (
              <span className="shrink-0" key={`Trigger-${trigger.index}-Segment-${index}`}>
                <Tooltip
                  interactive={true}
                  darkTheme={true}
                  placement="bottom"
                  tooltipContent={
                    <div>
                      <span className="block font-semibold">
                        {humanizePixelApi(segment.onHover.arguments?.[0]?.api)} event:
                      </span>
                      <span className="font-normal">{segment.onHover.arguments?.[0]?.event || 'Any event'}</span>
                    </div>
                  }
                >
                  <span className="cursor-pointer text-2xs font-normal underline decoration-cadet-blue-500 decoration-dashed underline-offset-2">
                    {segment.message}
                  </span>
                </Tooltip>
              </span>
            );
          } else if (segment.onHover.type === 'ecommerce_event') {
            return (
              <span className="shrink-0" key={`Trigger-${trigger.index}-Segment-${index}`}>
                <Tooltip
                  interactive={true}
                  darkTheme={true}
                  placement="bottom"
                  tooltipContent={
                    <div>
                      <span className="block font-semibold">
                        {humanizeEcommerceApi(segment.onHover.arguments?.[0]?.api)} event:
                      </span>
                      <span className="font-normal">{segment.onHover.arguments?.[0]?.event || 'Any event'}</span>
                    </div>
                  }
                >
                  <span className="cursor-pointer text-2xs font-normal underline decoration-cadet-blue-500 decoration-dashed underline-offset-2">
                    {segment.message}
                  </span>
                </Tooltip>
              </span>
            );
          }
          return (
            <span
              key={`${parsed.id}_${triggerIndex}_${index}`}
              className="text-link shrink-0 cursor-pointer text-2xs font-normal"
            >
              {segment?.message}
            </span>
          );
        }
        return (
          <span key={`${parsed.id}_${triggerIndex}_${index}`} className="shrink-0">
            {segment?.message}
          </span>
        );
      })
      .filter(Boolean);
  });

  return humanizedTriggers;
}

function textOnlyTriggersFromFilters(goal) {
  const parsed = buildDisplayDataFromTriggers(goal, goal?.triggers);

  if (!parsed.triggers?.length) {
    return null;
  }

  const triggers = parsed.triggers;

  const noDisplayList = ['|', 'Copy script'];

  const humanizedTriggers = triggers.map((trigger, triggerIndex) => {
    const msg = trigger.segments
      .reduce((acc, segment) => {
        const msg = typeof segment === 'string' ? segment : segment?.message;
        if (!msg || noDisplayList.includes(msg)) {
          return acc;
        } else {
          return acc + ' ' + msg;
        }
      }, '')
      .trim();

    if (msg) {
      return (
        <span key={`${parsed.id}_${triggerIndex}`} className="leading-tight line-clamp-2">
          {msg}
        </span>
      );
    }
    return null;
  });

  return humanizedTriggers;
}

export function GoalTriggerSummary({
  goal,
  textOnly = false,
  centered,
  expandBehavior = 'click',
  showMaxTriggers = 2,
  showIntroText = true,
  className,
}) {
  const [expanded, setExpanded] = useState(false);
  const hasTriggers = Array.isArray(goal?.triggers) && goal.triggers.length > 0;

  if (!hasTriggers) {
    return null;
  }

  const triggers = textOnly ? textOnlyTriggersFromFilters(goal) : triggersFromFilters(goal);

  if (triggers === null) {
    return null;
  }

  const truncatedTriggers = triggers.slice(0, showMaxTriggers ?? undefined);
  const leftOverTriggers = showMaxTriggers ? triggers.slice(showMaxTriggers) : [];
  const displayedTriggers = expanded ? triggers : truncatedTriggers;

  return (
    <div
      className={classNames('text-caption', className, {
        'w-full text-center': centered,
        'flex flex-col': !centered,
      })}
    >
      {showIntroText ? <span className="block">Conversion triggered when someone:</span> : null}

      <ul className="inline-block max-w-full list-outside list-disc pl-5 text-left ">
        {displayedTriggers.map((trigger, index) => (
          <li key={`Goal-${goal.id}_trigger_${index}`}>
            <div className="flex items-center space-x-1 overflow-hidden">
              {trigger}
              {displayedTriggers.length > index + 1 ? <span className="!ml-0 shrink-0">, or</span> : null}
            </div>
          </li>
        ))}
      </ul>

      {leftOverTriggers.length > 0 && expandBehavior === 'click' ? (
        <div
          className={classNames({
            'ml-5': displayedTriggers.length > 1,
            'self-start': !centered,
            'flex w-full items-center justify-center': centered,
          })}
        >
          <button type="button" className="font-semibold text-dodger-blue-500" onClick={() => setExpanded((x) => !x)}>
            {expanded ? 'Hide triggers' : `Show ${leftOverTriggers.length} more triggers`}
          </button>
        </div>
      ) : null}

      {leftOverTriggers.length > 0 && expandBehavior === 'info' ? (
        <div
          className={classNames('font-semibold', {
            'ml-5': displayedTriggers.length > 1,
            'self-start': !centered,
            'flex w-full items-center justify-center': centered,
          })}
        >
          +{leftOverTriggers.length} more triggers
        </div>
      ) : null}
    </div>
  );
}

export function GoalLinkURL({ url, message }) {
  const ref = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    setIsTruncated(ref.current?.clientWidth < ref.current?.scrollWidth);

    const getTruncated = () => {
      setIsTruncated(ref.current?.clientWidth < ref.current?.scrollWidth);
    };
    window.addEventListener('resize', getTruncated);

    return () => window.removeEventListener('resize', getTruncated);
  }, [message]);

  return (
    <Tooltip
      show={isTruncated}
      style={{ maxWidth: '230px' }}
      tooltipContent={<div className="break-words">{message}</div>}
    >
      <a
        href={url}
        className="text-link external-link relative flex flex-1 items-center text-2xs font-normal"
        target="_blank"
        rel="noopener noreferrer"
      >
        <span className="relative min-w-0 truncate" ref={ref}>
          {message}
        </span>
      </a>
    </Tooltip>
  );
}

export function parseTriggers(triggers) {
  return triggers.map((trigger, index) => {
    const { __typename, ...payload } = trigger;
    return {
      ...payload,
      ...deserializeTrigger(trigger.filter),
      name: trigger?.name ?? '',
      expanded: index === 0,
      valueType: trigger.valueType || GOAL_VALUE_TYPES.NO_VALUE,
      elementValue: trigger.elementValue
        ? {
            ...trigger.elementValue,
            syncSelectors: true,
          }
        : null,
    };
  });
}

const escapeHtml = (unsafe) => {
  return unsafe
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
};

export function generateCodeGoalScript(trigger, code, escaped = true) {
  if (
    trigger.valueType?.toUpperCase() === GOAL_VALUE_TYPES.CODE &&
    trigger.trigger?.toUpperCase() === GOAL_TRIGGER_TYPES.CODE
  ) {
    const baseScript = `<script type="text/javascript">\n\t(window.CE_API || (window.CE_API=[])).push(function(){\n\t\tCE2.converted("${code}", {\n\t\t\tworth: '99.95',\n\t\t\tcurrency: 'USD'\n\t\t});\n\t});\n</script>`;
    return escaped ? escapeHtml(baseScript) : baseScript;
  }

  const baseScript = `<script type="text/javascript">\n\t(window.CE_API || (window.CE_API=[])).push(function(){\n\t\tCE2.converted("${code}");\n\t});\n</script>`;
  return escaped ? escapeHtml(baseScript) : baseScript;
}

export function generateEcommerceGoalScript(type) {
  if (type === ECOMMERCE_EVENTS_LIST_TYPE.ADD_TO_CART) {
    return `&lt;script type=&quot;text/javascript&quot;&gt;
      // Call this code when someone adds something to the shopping cart
      (window.CE_API || (window.CE_API=[])).push(function(){
        CE2.addToCart(
          {
            name: 'Product 1',  // Required.
            price: 99.95,       // Required. Price in default or specified currency.
                                //  This is the total price for _all_ items
                                //  not an individual per-item price.
            currency: 'USD',    // Optional. Three-digit currency code.
            quantity: 2,        // Optional. Defaults to &quot;1&quot; if not provided.
            productId: '12345'  // Optional.
          }
        );
      });
    &lt;/script&gt;`;
  }

  if (type === ECOMMERCE_EVENTS_LIST_TYPE.START_CHECKOUT) {
    return `&lt;script type=&quot;text/javascript&quot;&gt;
      // Call this code when someone *starts* the checkout process
      (window.CE_API || (window.CE_API=[])).push(function(){
        CE2.startCheckout();
      });
    &lt;/script&gt;`;
  }

  if (type === ECOMMERCE_EVENTS_LIST_TYPE.COMPLETE_CHECKOUT) {
    return `&lt;script type=&quot;text/javascript&quot;&gt;
      // Call this code when someone *completed* the checkout process
      // and places an order
      (window.CE_API || (window.CE_API=[])).push(function(){
        CE2.checkoutCompleted(
          total: 99.95,      // Required. The final amount when checking out
                            //   in default or specified currency.
          currency: 'USD',   // Optional. Three-digit currency code.
          orderId: '4444'    // Optional.
        );
      });
    &lt;/script&gt;`;
  }

  return null;
}

function CopyCodeLink({ trigger, segment, noParse = false, theme = 'light' }) {
  const notifications = useNotifications();
  const isNoParse = noParse ? segment : segment.onClick.arguments[0];
  const script = useMemo(() => generateCodeGoalScript(trigger, isNoParse, false), [isNoParse, trigger]);

  function handleOnClick() {
    if (noParse) {
      copyToClipboard(script);
      notifications.success({ content: 'Script copied to clipboard', timeout: 2000 });
    } else {
      if (segment.onClick) {
        copyToClipboard(script);
        notifications.success({ content: 'Script copied to clipboard', timeout: 2000 });
      }
    }
  }

  return (
    <Tooltip
      interactive={false}
      darkTheme={true}
      placement="bottom"
      tooltipContent={<div className="w-40">Click to copy the script for this trigger</div>}
    >
      <button
        onClick={() => handleOnClick()}
        className={classNames('cursor-pointer', {
          'text-link text-2xs font-normal': !noParse && theme === 'light',
          'text-xs text-malibu-500 hover:underline': noParse && theme === 'dark',
          'text-dodger-blue-500 text-sm hover:underline': noParse && theme === 'light',
        })}
      >
        {noParse ? 'Copy script' : segment.message}
      </button>
    </Tooltip>
  );
}

export function generateRecordingsFilterUrlForGoal(data, selected = { special: SPECIAL_DATE_RANGES.LAST_30_DAYS }) {
  return JSON.stringify({
    version: RECORDINGS_FILTER_VERSION,
    operator: LOGICALS.AND,
    conditions: [
      {
        criteria: 'goal',
        comparison: 'did all of',
        values: [data.id],
      },
      {
        criteria: 'date_range',
        value: selected.special
          ? {
              special: selected.special,
            }
          : {
              start_date: selected.startDate,
              end_date: selected.endDate,
            },
      },
    ],
  });
}

const basicFields = ['autoName', 'devices', 'expanded', 'filter', 'name', 'syncSelectors', 'trigger'];
const elementFields = [
  'phoneSelector',
  'tabletSelector',
  'desktopSelector',
  'targetUrl',
  'trackingOption',
  'elementSelectorType',
  'wildcardUrl',
];

const getWhitelistedFields = (type) => {
  switch (type) {
    case 'CODE':
      return [...basicFields, 'code'];
    case 'PIXEL_EVENT':
      return [...basicFields, 'pixelApi', 'pixelCustomEvent', 'pixelEvent', 'pixelId'];
    case 'ECOMMERCE_EVENT':
      return [...basicFields, 'ecommerceApi', 'ecommerceEvent'];
    case 'CLICK':
    case 'FORM_SUBMISSION':
      return [...basicFields, ...elementFields];
    case 'URL_VISIT':
      return [...basicFields, 'targetUrl'];
    default:
      return basicFields;
  }
};

export function contextToArgs(context) {
  const { payload } = context;

  const triggers = payload.triggers.map((t) => pickFields(t, getWhitelistedFields(t.trigger)));

  return {
    ...payload,
    triggers,
  };
}

export function getTriggerContent(contentType, contentFilter) {
  const type = contentType.toUpperCase();
  const parsedFilter = safeJsonParse(contentFilter);
  const notAvailable = 'N/A';

  let content = {
    title: 'Other trigger type',
    text: notAvailable,
    urlAddress: '',
    isCode: false,
  };

  switch (type) {
    case GOAL_TRIGGER_TYPES.CLICK:
      return (content = {
        title: 'Clicks on a button or link',
        text: 'on',
        urlAddress: parsedFilter?.conditions[0]?.values ? parsedFilter.conditions[0].values[0].on_page : notAvailable,
        isCode: false,
      });
    case GOAL_TRIGGER_TYPES.CODE:
      return (content = {
        title: 'Triggers a script',
        text: '',
        urlAddress: '',
        isCode: true,
      });
    case GOAL_TRIGGER_TYPES.FORM_SUBMISSION:
      return (content = {
        title: 'Fills out a form',
        text: 'on',
        urlAddress: parsedFilter?.conditions[0]?.values ? parsedFilter.conditions[0].values[0].on_page : notAvailable,
        isCode: false,
      });
    case GOAL_TRIGGER_TYPES.URL_VISIT:
      return (content = {
        title: 'Reaches page',
        text: '',
        urlAddress: parsedFilter?.conditions[0]?.values ? parsedFilter.conditions[0].values[0] : notAvailable,
        isCode: false,
      });
    case GOAL_TRIGGER_TYPES.PIXEL_EVENT:
      return (content = {
        title: 'Triggers a conversion pixel',
        text: `${parsedFilter?.conditions[0].values ? humanizePixelApi(parsedFilter.conditions[0].values[0].api) : notAvailable} event`,
        urlAddress: '',
        isCode: false,
      });
    case GOAL_TRIGGER_TYPES.API:
      return (content = {
        title: 'Called via API',
        text: '',
        urlAddress: '',
        isCode: false,
      });
    case GOAL_TRIGGER_TYPES.ECOMMERCE_EVENT:
      return (content = {
        title: 'Completes an eCommerce event',
        text: `${parsedFilter?.conditions[0]?.values ? `${humanizeEcommerceApi(parsedFilter.conditions[0].values[0].api)} - ${capitalizeFirstLetter(camelToSnake(parsedFilter.conditions[0].values[0].event).replaceAll('_', ' '))}` : notAvailable}`,
        urlAddress: '',
        isCode: false,
      });
    default:
      return content;
  }
}

export function RenderTriggerText({ trigger, theme = 'dark', single = false, isDeleted = false }) {
  const filter = safeJsonParse(trigger.filter);
  const type = trigger.type ?? 'other';
  const codeHaveValue = filter.conditions[0]?.values ?? false;
  const { title, text, urlAddress, isCode } = getTriggerContent(type, filter);

  return (
    <>
      <span
        className={classNames('font-bold', {
          'text-xs mb-1': !single,
          'text-sm mb-0.25': single,
          'text-body-3': isDeleted,
        })}
      >
        {title}
        {isDeleted ? ` (deleted)` : ''}
      </span>

      {isCode && !isDeleted ? (
        <>
          {codeHaveValue ? (
            <CopyCodeLink trigger={trigger} segment={filter.conditions[0].values[0]} noParse={true} theme={theme} />
          ) : (
            <Tooltip
              interactive={true}
              darkTheme={true}
              placement="bottom"
              tooltipContent={
                <div className="w-40">
                  Read our{' '}
                  <a
                    href={SupportLinks.script.ce2Functions}
                    className="text-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    help article
                  </a>{' '}
                  on setting up code goals.
                </div>
              }
            >
              <span className="cursor-pointer text-2xs font-normal underline decoration-cadet-blue-500 decoration-dashed underline-offset-2">
                Conversion triggered
              </span>
            </Tooltip>
          )}
        </>
      ) : (
        <span
          className={classNames({
            'text-xs truncate': !single,
            'text-sm': single,
          })}
        >
          {text && `${text} `}
          {urlAddress && (
            <a
              href={urlAddress}
              className={classNames('hover:underline', {
                'text-malibu-500': theme === 'dark',
                'text-dodger-blue-500': theme === 'light',
              })}
              target="_blank"
              rel="noopener noreferrer"
            >
              {urlAddress}
            </a>
          )}
        </span>
      )}
    </>
  );
}

export function combineTriggersWithSegments(goal, countsData) {
  const triggers = goal?.triggers ?? [];
  const parsedTriggers = buildDisplayDataFromTriggers(goal, triggers);
  const segmentTriggers = parsedTriggers?.triggers ?? [];

  const result = triggers.map((trigger, i) => {
    const foundTrigger = segmentTriggers.find((item) => item.id === trigger.id);
    const foundTriggerCounts = countsData?.find((item) => item.id === trigger.id) ?? {};

    return {
      ...trigger,
      segments: foundTrigger?.segments ?? [],
      counts: { ...foundTriggerCounts },
      position: i,
    };
  });

  return result.filter((trigger) => {
    // only show deleted triggers with data
    return !trigger.deletedAt || trigger.counts?.conversionsCount > 0;
  });
}

export function goalType(goal) {
  const activeTriggers = Array.isArray(goal?.triggers)
    ? goal.triggers.filter((trigger) => !trigger?.deletedAt && !trigger?.deleted_at)
    : [];

  if (!activeTriggers.length) {
    return 'OTHER';
  }

  const criteria = activeTriggers.flatMap((trigger) => {
    const conditions = safeJsonParse(trigger?.filter)?.conditions;

    if (Array.isArray(conditions) && conditions.length > 0) {
      return conditions
        .map((condition) => condition?.criteria)
        .filter((value) => typeof value === 'string' && value.trim() !== '')
        .map((value) => value.trim().toLowerCase());
    }

    return [trigger?.type, trigger?.trigger]
      .filter((value) => typeof value === 'string' && value.trim() !== '')
      .map((value) => value.trim().toLowerCase());
  });

  const uniqueCriteria = [...new Set(criteria)];

  if (uniqueCriteria.length !== 1) {
    return 'OTHER';
  }

  const [singleCriteria] = uniqueCriteria;
  const matchingType = Object.values(GOAL_TRIGGER_TYPES).find((type) => type.toLowerCase() === singleCriteria);

  return matchingType ?? 'OTHER';
}
